import React, { useState, useEffect } from 'react';
import { showsAPI, vmixAPI, singularAPI } from '../../lib/api';
import { useShowStore } from '../../stores/showStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { useThemeStore } from '../../stores/themeStore';
import { useSettingsStore, effectiveRelativeTimes } from '../../stores/settingsStore';

const BASE_URL = window.location.origin;

const BRIDGE_JS_URL = 'https://mission-control-production-851c.up.railway.app/bridge.js';

export default function ShowSettings({ show, showId, onClose }) {
  const [vmixHost, setVmixHost] = useState(show?.vmix_host || '');
  const [vmixPort, setVmixPort] = useState(show?.vmix_port || 8088);
  const [downloadingLauncher, setDownloadingLauncher] = useState(false);
  const [singularToken, setSingularToken] = useState('');
  // A stored token can't be detected from the show row: it is redacted from REST
  // ('[redacted]') and absent from the local-first sync rules entirely, so under
  // local-first show.singular_api_key is ALWAYS undefined. The authoritative
  // answer comes from GET /api/singular/status via the connection store —
  // without it this panel offered to re-enter a token that was already saved.
  const singularConfigured = useConnectionStore(s => s.singularConfigured) || !!show?.singular_api_key;
  const [singularTokenEditing, setSingularTokenEditing] = useState(false);
  // Start in edit mode only once we know there is nothing saved.
  useEffect(() => { if (!singularConfigured) setSingularTokenEditing(true); }, [singularConfigured]);
  const tokenAlreadySaved = !singularTokenEditing && singularConfigured;
  const [showSingularToken, setShowSingularToken] = useState(false);
  const [streamdeckToken, setStreamdeckToken] = useState(show?.streamdeck_token || null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { setShow } = useShowStore.getState();
  const { setSingularCompositions } = useConnectionStore.getState();
  const theme = useThemeStore(s => s.theme);
  const setTheme = useThemeStore(s => s.setTheme);
  const uiStyle = useSettingsStore(s => s.uiStyle);
  const setUiStyle = useSettingsStore(s => s.setUiStyle);
  const relativeTimes = useSettingsStore(s => s.relativeTimes);
  const setRelativeTimes = useSettingsStore(s => s.setRelativeTimes);
  const relativeOn = effectiveRelativeTimes(relativeTimes, uiStyle);
  const typeAccent = useSettingsStore(s => s.typeAccent);
  const setTypeAccent = useSettingsStore(s => s.setTypeAccent);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const updateData = {
        vmix_host: vmixHost.trim() || null,
        vmix_port: parseInt(vmixPort) || 8088,
      };

      // Only update singular_api_key if user actually typed a new token
      if (singularTokenEditing && singularToken.trim()) {
        updateData.singular_api_key = singularToken.trim();
      }

      const res = await showsAPI.update(showId, updateData);
      setShow(res.data);

      if (vmixHost.trim()) {
        vmixAPI.connect(showId).catch(() => {});
      }

      if (updateData.singular_api_key) {
        singularAPI.refresh(showId).catch(() => {});
        // Re-probe so the stored-token flag and the connection indicator reflect
        // the token just saved, then reload the compositions the cue-action
        // dropdowns read from.
        singularAPI.status(showId)
          .then(({ data }) => {
            useConnectionStore.getState().setSingularStatus(data);
            if (!data.connected) return;
            return singularAPI.compositions(showId).then(r => setSingularCompositions(r.data));
          })
          .catch(() => {});
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  }

  // The operator is signed in and looking at one show, so the launcher has
  // everything it needs already — asking them to also produce a credential was
  // asking them to know a deployment secret. The token is fetched here instead,
  // and is the same one on every download so a .bat already sitting on the vMix
  // machine keeps working.
  async function handleDownloadLauncher() {
    setDownloadingLauncher(true);
    setError('');
    let secret;
    try {
      const res = await showsAPI.bridgeToken(showId);
      secret = res.data.bridge_token;
    } catch (err) {
      setError(err.response?.data?.error || 'Could not get a bridge token for this show.');
      return;
    } finally {
      setDownloadingLauncher(false);
    }

    const showName = (show?.name || 'show').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const host = vmixHost.trim() || 'localhost';
    const port = parseInt(vmixPort) || 8088;

    const bat = [
      '@echo off',
      'title Mission Control LocalBridge',
      'echo ================================',
      'echo  Mission Control LocalBridge',
      `echo  Show: ${show?.name || showId}`,
      `echo  vMix: ${host}:${port}`,
      'echo ================================',
      'echo.',
      '',
      'REM Always fetch the latest bridge.js so restarts pick up updates (e.g. new',
      'REM vMix features). Download to a temp file first so a failed download never',
      'REM clobbers a working copy; fall back to the existing bridge.js if offline.',
      'echo Updating bridge.js...',
      `powershell -Command "try { Invoke-WebRequest -Uri '${BRIDGE_JS_URL}' -OutFile '%~dp0bridge.js.new' -UseBasicParsing -ErrorAction Stop; Move-Item -Force '%~dp0bridge.js.new' '%~dp0bridge.js' } catch { Write-Host 'Could not download latest bridge.js; using existing copy if present.' }"`,
      'if not exist "%~dp0bridge.js" (',
      '    echo No bridge.js available and the download failed. Check your internet connection.',
      '    pause',
      '    exit /b 1',
      ')',
      'echo.',
      '',
      'echo Starting bridge... Press Ctrl+C to stop.',
      'echo.',
      `node "%~dp0bridge.js" --secret ${secret} --show ${showId} --vmix ${host} --port ${port}`,
      '',
      'pause',
    ].join('\r\n');

    const blob = new Blob([bat], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `liverundown-bridge-${showName}.bat`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleGenerateToken() {
    setGeneratingToken(true);
    try {
      const res = await showsAPI.generateStreamdeckToken(showId);
      setStreamdeckToken(res.data.streamdeck_token);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setGeneratingToken(false);
    }
  }

  function handleCopyToken(text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 1500);
    });
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') onClose();
    e.stopPropagation();
  }

  const canDownload = vmixHost.trim();

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-app-panel border border-app-border rounded-lg w-full max-w-sm max-h-[90vh] flex flex-col" onKeyDown={handleKeyDown}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-app-border shrink-0">
          <h2 className="font-medium text-app-fg">Show Settings</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-app-fg transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          {error && (
            <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Appearance */}
          <div className="space-y-3">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Appearance</div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Theme</span>
              <div className="flex rounded border border-app-border overflow-hidden">
                {[
                  { value: 'dark', label: 'Dark' },
                  { value: 'light', label: 'Light' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTheme(opt.value)}
                    className={`px-4 py-1.5 text-xs font-medium transition-colors ${
                      theme === opt.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-app-surface text-gray-400 hover:text-app-fg hover:bg-app-hover'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Light theme improves readability in bright locations. Applies to the live show view on this device.
            </p>

            {/* Producer style — orthogonal to theme; same data, actions & hotkeys */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Producer style</span>
              <div className="flex rounded border border-app-border overflow-hidden">
                {[
                  { value: 'standard', label: 'Standard' },
                  { value: 'modern', label: 'Modern' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setUiStyle(opt.value)}
                    className={`px-4 py-1.5 text-xs font-medium transition-colors ${
                      uiStyle === opt.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-app-surface text-gray-400 hover:text-app-fg hover:bg-app-hover'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Modern is a calmer, presentation-first layout for running the show live. Standard is the classic dense editing table.
            </p>

            {/* Relative times — IN shows a countdown instead of a wall clock */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Relative times</span>
              <button
                type="button"
                onClick={() => setRelativeTimes(!relativeOn)}
                className={`px-4 py-1.5 text-xs font-medium rounded border transition-colors ${
                  relativeOn
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-app-surface border-app-border text-gray-400 hover:text-app-fg hover:bg-app-hover'
                }`}
              >
                {relativeOn ? 'On' : 'Off'}
              </button>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Show the IN column as a countdown (“in 02:38”) rather than a wall-clock time. Cues already taken keep their clock time. Defaults on in Modern, off in Standard.
            </p>

            {/* Cue-type accent — A/B: label outline box vs coloured row edge */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Cue-type accent</span>
              <div className="flex rounded border border-app-border overflow-hidden">
                {[
                  { value: 'outline', label: 'Outline' },
                  { value: 'edge', label: 'Row edge' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTypeAccent(opt.value)}
                    className={`px-4 py-1.5 text-xs font-medium transition-colors ${
                      typeAccent === opt.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-app-surface text-gray-400 hover:text-app-fg hover:bg-app-hover'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              How a cue’s type colour shows on the standard rows: a box around the label, or a coloured left edge on the row (like the live/next highlight). Standard view only.
            </p>
          </div>

          <div className="border-t border-app-border" />

          {/* vMix connection */}
          <div className="space-y-3">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">vMix Connection</div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                Host IP
              </label>
              <input
                type="text"
                value={vmixHost}
                onChange={e => setVmixHost(e.target.value)}
                placeholder="192.168.1.x or localhost"
                autoFocus
                className="w-full bg-app-bg border border-app-border rounded px-3 py-2 text-sm text-app-fg placeholder-gray-600 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                HTTP Port
              </label>
              <input
                type="number"
                value={vmixPort}
                onChange={e => setVmixPort(e.target.value)}
                placeholder="8088"
                className="w-full bg-app-bg border border-app-border rounded px-3 py-2 text-sm text-app-fg placeholder-gray-600 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="border-t border-app-border" />

          {/* Singular.live connection */}
          <div className="space-y-3">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Singular.live</div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                Control Token
              </label>

              {/* Token already saved — show badge + change button */}
              {tokenAlreadySaved ? (
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 text-sm text-green-400">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Token saved
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSingularToken(''); setSingularTokenEditing(true); }}
                    className="text-xs text-gray-500 hover:text-gray-300 underline transition-colors"
                  >
                    Change
                  </button>
                </div>
              ) : (
                /* Editing mode — show input + eye toggle */
                <div className="flex gap-2">
                  <input
                    type={showSingularToken ? 'text' : 'password'}
                    value={singularToken}
                    onChange={e => setSingularToken(e.target.value)}
                    placeholder="Paste app control token"
                    autoFocus
                    className="flex-1 bg-app-bg border border-app-border rounded px-3 py-2 text-sm text-app-fg placeholder-gray-600 focus:outline-none focus:border-blue-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSingularToken(v => !v)}
                    className="px-2 text-gray-600 hover:text-gray-400 transition-colors"
                    title={showSingularToken ? 'Hide token' : 'Show token'}
                  >
                    {showSingularToken ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                  {singularConfigured && (
                    <button
                      type="button"
                      onClick={() => { setSingularToken(''); setSingularTokenEditing(false); }}
                      className="px-2 text-gray-600 hover:text-gray-400 transition-colors text-xs"
                      title="Cancel — keep existing token"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">
                Found in Singular.live → App settings → Control apps → your app token.
              </p>
            </div>
          </div>

          <div className="border-t border-app-border" />

          {/* StreamDeck */}
          <div className="space-y-3">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">StreamDeck</div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Use these URLs in StreamDeck "Website" buttons to control the show. No login required — the token is the key.
            </p>

            {streamdeckToken ? (
              <div className="space-y-2">
                <div className="bg-app-bg border border-app-border rounded p-2.5 space-y-1.5">
                  {['take', 'back', 'skip', 'start', 'reset'].map(action => (
                    <div key={action} className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs w-10 shrink-0">{action}</span>
                      <code className="flex-1 text-xs text-blue-400 truncate font-mono">
                        {BASE_URL}/api/sd/{streamdeckToken}/{action}
                      </code>
                      <button
                        onClick={() => handleCopyToken(`${BASE_URL}/api/sd/${streamdeckToken}/${action}`)}
                        className="text-gray-600 hover:text-gray-300 transition-colors shrink-0"
                        title="Copy URL"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                {copiedToken && <p className="text-xs text-green-500">Copied!</p>}
                <button
                  onClick={handleGenerateToken}
                  disabled={generatingToken}
                  className="text-xs text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-40"
                >
                  {generatingToken ? 'Regenerating...' : 'Regenerate token (invalidates old URLs)'}
                </button>
              </div>
            ) : (
              <button
                onClick={handleGenerateToken}
                disabled={generatingToken}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-app-surface hover:bg-app-hover disabled:opacity-40 border border-app-border hover:border-app-border-strong text-gray-300 text-sm font-medium rounded transition-colors"
              >
                {generatingToken ? 'Generating...' : 'Generate StreamDeck Token'}
              </button>
            )}
          </div>

          <div className="border-t border-app-border" />

          {/* Bridge launcher */}
          <div className="space-y-3">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Bridge Launcher</div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Download a <span className="text-gray-400">.bat</span> file for this show. Double-click it on the vMix machine — it downloads and starts the bridge automatically.
            </p>

            <button
              onClick={handleDownloadLauncher}
              disabled={!canDownload || downloadingLauncher}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-app-surface hover:bg-app-hover disabled:opacity-40 border border-app-border hover:border-app-border-strong text-gray-300 text-sm font-medium rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {downloadingLauncher ? 'Preparing...' : 'Download Launcher (.bat)'}
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-app-border shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-app-fg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
          >
            {saving ? 'Saving...' : 'Save & Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}
